import { BadgeCheck, Compass, Hammer, Sprout } from "lucide-react";
import { BRAND } from "@/data/content";

const VALUES = [
  { icon: Compass, title: "Start with understanding", text: "We build the foundations before moving into practice." },
  { icon: Hammer, title: "Learn with purpose", text: "Lessons connect to real tasks, real exams and real work." },
  { icon: Sprout, title: "Keep growing", text: "Pathways across subjects, technology and practical skills." },
];

export const About = () => {
  return (
    <section id="about" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24" data-testid="about-section">
      <div className="grid items-start gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <p className="eyebrow-label" data-testid="about-eyebrow">About the academy</p>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl" data-testid="about-heading">
            Rooted in experience. <span className="text-secondary">Focused on possibility.</span>
          </h2>

          <div className="mt-8 rounded-2xl border border-border bg-white p-8 shadow-sm" data-testid="about-founder-card">
            <div className="flex items-center gap-4">
              <img src="/bsa-logo.png" alt="BSA Skills Academy logo" className="h-14 w-14 rounded-2xl border border-border object-contain p-1" />
              <div>
                <p className="font-heading text-lg font-bold text-slate-900">{BRAND.founder}</p>
                <p className="text-sm text-muted-foreground">Founder, {BRAND.fullName}</p>
              </div>
            </div>
            <p className="mt-5 text-sm leading-relaxed text-muted-foreground sm:text-base">
              {BRAND.fullName} was established in {BRAND.founded} by Joseph Mhlongo, bringing together his experience
              in electrical engineering, maintenance and business. The same practical philosophy behind BSA's CAD and
              skills programmes now supports school learners through tutoring.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <span className="rounded-full bg-secondary/10 px-3 py-1 text-xs font-bold text-secondary">Est. {BRAND.founded}</span>
              <span className="rounded-full bg-secondary/10 px-3 py-1 text-xs font-bold text-secondary">Witbank, Mpumalanga</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="rounded-2xl bg-primary p-8 sm:p-10" data-testid="about-tutors-card">
            <div className="flex items-start gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-500 text-slate-900">
                <BadgeCheck className="h-6 w-6" />
              </span>
              <div>
                <h3 className="font-heading text-xl font-bold text-white sm:text-2xl">
                  Qualified, experienced tutors.
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-white/80 sm:text-base">
                  Our tutors have the necessary experience and relevant qualifications in the subjects they teach.
                  Lessons run in small groups, so every learner is seen, heard and helped — with the hands-on teaching
                  style BSA is known for, not just theory.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 grid gap-5 sm:grid-cols-3" data-testid="about-values">
            {VALUES.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="rounded-2xl border border-border bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
                  <Icon className="h-5 w-5" />
                </span>
                <h4 className="mt-4 font-heading text-base font-bold text-slate-900">{title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{text}</p>
              </div>
            ))}
          </div>

          <p className="mt-8 border-l-4 border-amber-500 pl-5 font-heading text-lg font-bold text-slate-800 sm:text-xl" data-testid="about-tagline">
            "{BRAND.tagline}" <span className="block text-sm font-semibold text-muted-foreground">Practical learning. Real possibilities.</span>
          </p>
        </div>
      </div>
    </section>
  );
};
